import { CaretUpFilled, FilterFilled, PlusOutlined } from '@ant-design/icons';
import { Badge, Button, Spin, Tabs, Tooltip } from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import ContentCard from '../../components/ContentCard';
import PaginationMeta from '../../types/PaginationMeta';
import CreateUserModal from '../components/CreateUserModal';
import InstructorMap from '../components/InstructorMap';
import UserFilter from '../components/UserFilter';
import UserResults from '../components/UserResults';
import { Permission } from '../types/Permission';
import User from '../types/User';
import UserAPI, { DEFAULT_USER_PAGE_SIZE, UserFilterParams } from '../UserAPI';
import { useUser } from '../userContext';
import { userHasPermission } from '../userUtils';

const Users: React.FC = () => {
  const location = useLocation();
  const [currentUser] = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(
    null,
  );
  const [currentFilter, setCurrentFilter] = useState<UserFilterParams>({});
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [isFilterVisible, setFilterVisible] = useState<boolean>(false);
  const [isFilterPristine, setFilterPristine] = useState<boolean>(true);
  const activeRequestCountRef = useRef(0);
  const activeRequestKeysRef = useRef<Set<string>>(new Set());
  const latestListRequestIdRef = useRef(0);

  const { t } = useTranslation();

  const fetchUsers = useCallback(
    async (_filter: UserFilterParams, page = 1) => {
      const requestKey = JSON.stringify({ filter: _filter, page });
      if (activeRequestKeysRef.current.has(requestKey)) {
        return;
      }

      activeRequestKeysRef.current.add(requestKey);
      activeRequestCountRef.current += 1;
      const listRequestId =
        page === 1
          ? latestListRequestIdRef.current + 1
          : latestListRequestIdRef.current;

      if (page === 1) {
        latestListRequestIdRef.current = listRequestId;
      }

      setLoadingUsers(true);
      if (page === 1) {
        setUsers([]);
        setPaginationMeta(null);
      }
      try {
        const { data } = await UserAPI.getPaginatedUsers({
          ..._filter,
          page,
          pageSize: DEFAULT_USER_PAGE_SIZE,
        });

        if (listRequestId !== latestListRequestIdRef.current) {
          return;
        }

        setUsers((currentUsers) =>
          page === 1 ? data.data : [...currentUsers, ...data.data],
        );
        setPaginationMeta(data.paginationMeta);
      } finally {
        activeRequestKeysRef.current.delete(requestKey);
        activeRequestCountRef.current -= 1;
        setLoadingUsers(activeRequestCountRef.current > 0);
      }
    },
    [],
  );

  const onFilterChange = useCallback(
    (filter: UserFilterParams, pristine: boolean) => {
      setFilterPristine(pristine);
      setCurrentFilter(filter);
      fetchUsers(filter, 1);
      if (!pristine) {
        setFilterVisible(true);
      }
    },
    [fetchUsers],
  );

  const getLink = useCallback(
    (user: User) => {
      return `${location.pathname}/${user.id}`;
    },
    [location.pathname],
  );

  const getInitialCenter = useCallback(() => {
    const lat = currentUser?.market.mapCenterLatitude;
    const lng = currentUser?.market.mapCenterLongitude;
    return lat && lng ? { lat, lng } : undefined;
  }, [
    currentUser?.market.mapCenterLatitude,
    currentUser?.market.mapCenterLongitude,
  ]);

  const tabItems = useMemo(
    () => [
      {
        key: 'list',
        label: t('common.list'),
        children: (
          <InfiniteScroll
            loadMore={() => {
              if (!loadingUsers) {
                fetchUsers(currentFilter, (paginationMeta?.page ?? 1) + 1);
              }
            }}
            hasMore={
              !loadingUsers &&
              (paginationMeta?.page ?? 1) < (paginationMeta?.lastPage ?? 1)
            }>
            <UserResults
              users={users}
              loading={loadingUsers}
              getLink={getLink}
            />
            {loadingUsers && (
              <div className="flex justify-center py-4">
                <Spin />
              </div>
            )}
          </InfiniteScroll>
        ),
      },
      {
        key: 'map',
        label: t('views.Users.instructorMap'),
        children: <InstructorMap initialCenter={getInitialCenter()} />,
      },
    ],
    [
      currentFilter,
      fetchUsers,
      getInitialCenter,
      getLink,
      loadingUsers,
      paginationMeta?.lastPage,
      paginationMeta?.page,
      t,
      users,
    ],
  );

  const onCreateUser = useCallback(() => {
    setShowUserModal(false);
    fetchUsers(currentFilter, 1);
  }, [currentFilter, fetchUsers]);

  const hideUserModal = useCallback(() => {
    setShowUserModal(false);
  }, []);

  const presentUserModal = useCallback(() => {
    setShowUserModal(true);
  }, []);

  const hideUserFilter = useCallback(() => {
    setFilterVisible(false);
  }, []);

  const presentUserFilter = useCallback(() => {
    setFilterVisible(true);
  }, []);

  return (
    <>
      <div className="flex justify-between items-center">
        <h2>{t('common.users')}</h2>
        <div>
          {isFilterVisible ? (
            <Badge dot={!isFilterPristine}>
              <Tooltip placement="top" title={t('common.filter')}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<CaretUpFilled />}
                  onClick={hideUserFilter}
                />
              </Tooltip>
            </Badge>
          ) : (
            <Badge dot={!isFilterPristine}>
              <Tooltip placement="top" title={t('common.filter')}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<FilterFilled />}
                  onClick={presentUserFilter}
                />
              </Tooltip>
            </Badge>
          )}
          {userHasPermission(currentUser, Permission.USER_CREATE) && (
            <Button
              type="primary"
              className="text-sm font-medium ml-2"
              icon={<PlusOutlined />}
              onClick={presentUserModal}>
              {t('views.Users.newUser')}
            </Button>
          )}
        </div>
      </div>
      <UserFilter visible={isFilterVisible} onChange={onFilterChange} />
      <ContentCard>
        <Tabs defaultActiveKey="list" size="large" items={tabItems} />
      </ContentCard>
      {showUserModal &&
        userHasPermission(currentUser, Permission.USER_CREATE) && (
          <CreateUserModal
            visible={showUserModal}
            onCreateUser={onCreateUser}
            onCancel={hideUserModal}
          />
        )}
    </>
  );
};

export default Users;
